import { useState } from "react";
import { books } from "@/mocks/books";

const categories = [
  "All",
  "SAT Prep",
  "Harvard",
  "Yale",
  "Cambridge",
  "Oxford",
  "MIT",
  "Stanford",
  "Columbia",
  "Wharton",
  "Chicago",
  "Princeton",
  "Business",
  "Marketing",
  "E-Commerce",
  "Trade",
];

const EducationSection = () => {
  const [cartItems, setCartItems] = useState<number[]>([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [showAll, setShowAll] = useState(false);

  const handleAddToCart = (id: number) => {
    setCartItems((prev) => [...prev, id]);
  };

  const filtered = activeCategory === "All"
    ? books
    : books.filter((b) => b.category === activeCategory);

  const displayed = showAll ? filtered : filtered.slice(0, 10);

  return (
    <section id="education" className="py-16 bg-[#fdf8f8]">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-1 bg-[#D4AF37] rounded-full"></div>
              <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Learn &amp; Grow</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
              Education &amp; Business Books
            </h2>
            <p className="text-gray-500 mt-2 text-sm">
              SAT prep, Ivy League university publications, e-commerce, dropshipping &amp; wholesale trading guides.
            </p>
          </div>
          <button className="flex items-center gap-2 border-2 border-[#D4AF37] text-[#D4AF37] px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#D4AF37] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-book-open-line"></i> View All Books
          </button>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setShowAll(false); }}
              className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-[#C41E3A] hover:text-[#C41E3A]"
              }`}
            >
              {cat === "SAT Prep" && <i className="ri-pencil-ruler-2-line mr-1"></i>}
              {["Harvard","MIT","Yale","Stanford","Oxford","Columbia","Wharton","Chicago","Princeton","Cambridge"].includes(cat) && <i className="ri-graduation-cap-line mr-1"></i>}
              {cat}
            </button>
          ))}
        </div>

        {/* Books Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {displayed.map((book) => (
            <div key={book.id} className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-[#D4AF37] transition-all group cursor-pointer">
              {/* Cover */}
              <div className="relative h-52 overflow-hidden bg-gray-50">
                <img
                  src={book.cover}
                  alt={book.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className={`absolute top-2 left-2 text-white text-[10px] font-bold px-2 py-1 rounded ${
                  book.category === "SAT Prep" ? "bg-[#1a3a6b]" :
                  ["Harvard","MIT","Yale","Stanford","Oxford","Columbia","Wharton","Chicago","Princeton","Cambridge"].includes(book.category) ? "bg-[#D4AF37] text-[#1a0a0a]" :
                  "bg-[#C41E3A]"
                }`}>
                  {book.category}
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-bold text-gray-900 text-xs leading-tight mb-2 line-clamp-2">{book.title}</h3>
                <div className="text-xs text-gray-400 mb-2 line-clamp-1">by {book.author}</div>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-3 h-3 flex items-center justify-center">
                      <i className={i < Math.floor(book.rating) ? "ri-star-fill text-[#D4AF37] text-[10px]" : "ri-star-line text-[#D4AF37] text-[10px]"}></i>
                    </div>
                  ))}
                  <span className="text-[10px] text-gray-400">({book.sold.toLocaleString()})</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base font-extrabold text-[#C41E3A]">${book.price}</span>
                  <span className="text-xs text-gray-400 line-through">${book.originalPrice}</span>
                </div>
                <button
                  onClick={() => handleAddToCart(book.id)}
                  className={`w-full text-xs py-2 rounded-md font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    cartItems.includes(book.id)
                      ? "bg-green-500 text-white"
                      : "bg-[#D4AF37] text-white hover:bg-[#b8962e]"
                  }`}
                >
                  {cartItems.includes(book.id) ? (
                    <><i className="ri-check-line mr-1"></i>Added</>
                  ) : (
                    <><i className="ri-shopping-bag-line mr-1"></i>Add to Cart</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Show More */}
        {filtered.length > 10 && !showAll && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 border-2 border-[#C41E3A] text-[#C41E3A] px-8 py-3 rounded-full text-sm font-bold hover:bg-[#C41E3A] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-down-line"></i>
              Show All {filtered.length} Books
            </button>
          </div>
        )}

        {/* University Highlight Strip */}
        <div className="mt-10 bg-white border border-gray-100 rounded-xl p-5">
          <div className="text-center mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Featured University Publishers</span>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {["Harvard", "Yale", "Cambridge", "Oxford", "MIT", "Stanford", "Columbia", "Wharton", "Chicago", "Princeton"].map((uni) => (
              <button
                key={uni}
                onClick={() => { setActiveCategory(uni); setShowAll(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="text-sm font-extrabold text-gray-400 hover:text-[#C41E3A] transition-colors cursor-pointer whitespace-nowrap"
              >
                {uni}
              </button>
            ))}
          </div>
        </div>

        {/* Banner */}
        <div className="mt-8 bg-gradient-to-r from-[#1a0a0a] to-[#3d1515] rounded-lg p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-[#D4AF37] text-sm font-semibold mb-2">FREE RESOURCE</div>
            <h3 className="text-white text-xl font-bold mb-2">Download Our Free Dropshipping Starter Guide</h3>
            <p className="text-gray-400 text-sm">Everything you need to start your USA &amp; Canada dropshipping business today.</p>
          </div>
          <button className="bg-[#D4AF37] text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-[#b8962e] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0">
            <i className="ri-download-line mr-2"></i>Download Free Guide
          </button>
        </div>
      </div>
    </section>
  );
};

export default EducationSection;
